
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { DateRecommendationList } from "./DateRecommendationList";
import { VenueRecommendationList } from "./VenueRecommendationList";
import { useState } from "react";
import { useRecommendationModal } from "./useRecommendationModal";
import { DateRecommendation, VenueRecommendation } from "./types";
import { useIsMobile } from "@/hooks/use-mobile";
import { RSVPButton } from "@/components/ui/rsvp-button";
import { TimePreferenceSection } from "./TimePreferenceSection";

interface RecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  dateRecommendations: DateRecommendation[];
  venueRecommendations: VenueRecommendation[];
}
export const RecommendationModal = ({
  isOpen,
  onClose,
  eventId,
  eventName,
  dateRecommendations,
  venueRecommendations
}: RecommendationModalProps) => {
  const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [selectedTimePreference, setSelectedTimePreference] = useState<string | undefined>(undefined);
  const [selectedTimeLabel, setSelectedTimeLabel] = useState<string | undefined>(undefined);
  
  const isMobile = useIsMobile();
  const {
    confirmSelections,
    isSubmitting,
    guests,
    isLoadingGuests,
    updateTimePreference
  } = useRecommendationModal(eventId);
  
  const handleConfirm = async () => {
    if (!selectedDateId || !selectedVenueId) return;
    
    await confirmSelections(selectedDateId, selectedVenueId, selectedTimeLabel);
    onClose();
  };
  
  const handleTimePreferenceSelect = (preference: string, label: string) => {
    setSelectedTimePreference(preference);
    setSelectedTimeLabel(label);
    updateTimePreference(label);
  };

  // Get the selected date's time of day
  const selectedDate = dateRecommendations.find(
    rec => rec.recommendation_id === selectedDateId
  );
  
  return <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
      <DrawerContent className="max-h-[85vh] flex flex-col">
        <DrawerHeader className="px-4 z-10 bg-black/0">
          <DrawerTitle className="text-left">Review Recommendations for {eventName}</DrawerTitle>
        </DrawerHeader>
        
        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-2 space-y-8">
          <DateRecommendationList 
            recommendations={dateRecommendations} 
            selectedId={selectedDateId} 
            onSelect={setSelectedDateId} 
            guests={guests || []} 
          />
          
          {/* Add the TimePreferenceSection between date and venue selections */}
          <TimePreferenceSection
            selectedDateId={selectedDateId}
            timeOfDay={selectedDate?.time_of_day}
            selectedTimePreference={selectedTimePreference}
            onTimePreferenceSelect={handleTimePreferenceSelect}
          />
          
          <VenueRecommendationList 
            recommendations={venueRecommendations} 
            selectedId={selectedVenueId} 
            onSelect={setSelectedVenueId} 
          />
        </div>
        
        <div className="px-4 py-4 mt-auto flex justify-center">
          <RSVPButton 
            onClick={handleConfirm} 
            disabled={!selectedDateId || !selectedVenueId || isSubmitting}
          >
            Confirm Selections
          </RSVPButton>
        </div>
      </DrawerContent>
    </Drawer>;
};
